import React, { Component } from 'react';
import { createFragmentContainer, graphql } from 'react-relay';
import ReactHighcharts from 'react-highcharts';
import { injectIntl } from 'react-intl';
import moment from 'moment';
import getTranslatedText from '../../services/getTranslatedText';
import {
  tntBlue,
  tntGreen,
  tntFadedGreen,
  tntFadedRed,
  tntRed,
  tntOrange,
  tntPink,
  tntPurple,
  tntYellow,
  tntTeal,
} from '../../theme/tntColors';
import { grey400, grey500 } from 'material-ui/styles/colors';

import saddestSmiley from '../../img/smileys/saddest.svg';
import sadSmiley from '../../img/smileys/sad.svg';
import happySmiley from '../../img/smileys/happy.svg';
import happiestSmiley from '../../img/smileys/happiest.svg';

import star1 from '../../img/stars/1.png';
import star2 from '../../img/stars/2.png';
import star3 from '../../img/stars/3.png';
import star4 from '../../img/stars/4.png';
import star5 from '../../img/stars/5.png';
import { QUESTION_TYPE_FIVESTARS, QUESTION_TYPE_NPS, QUESTION_TYPE_SMILEYCOMMENT, QUESTION_TYPE_SMILEYQUARTET, QUESTION_TYPE_YESNO } from '../../constants';

function getCategories(questionType, distribution, intl) {
  const yesNoCategories = {
    1: intl.formatMessage({
      id: 'no',
      defaultMessage: 'No',
    }),
    2: intl.formatMessage({
      id: 'yes',
      defaultMessage: 'Yes',
    }),
  };
  return distribution.map((opt) => {
    switch (questionType) {
      case QUESTION_TYPE_FIVESTARS:
      case QUESTION_TYPE_SMILEYQUARTET:
      case QUESTION_TYPE_SMILEYCOMMENT:
        return opt.value;
      case QUESTION_TYPE_YESNO:
        return yesNoCategories[opt.value];
      default:
        return getTranslatedText(opt.text, intl.locale) || opt.value;
    }
  });
}

function getSeriesColor(questionType, index) {
  switch (questionType) {
    case QUESTION_TYPE_FIVESTARS:
      const starColors = [tntRed, tntFadedRed, tntYellow, tntFadedGreen, tntGreen];
      return starColors[index];
    case QUESTION_TYPE_SMILEYQUARTET:
    case QUESTION_TYPE_SMILEYCOMMENT:
      const smileyColors = [tntRed, tntFadedRed, tntFadedGreen, tntGreen];
      return smileyColors[index];
    case QUESTION_TYPE_YESNO:
      const yesNoColors = [tntRed, tntGreen];
      return yesNoColors[index];
    case QUESTION_TYPE_NPS:
      function getNpsColor(value) {
        if (value <= 6) {
          return tntRed;
        } else if (value >= 7 && value <= 8) {
          return grey400;
        } else {
          return tntGreen;
        }
      }
      return getNpsColor(index + 1);
    default:
      const seriesColors = [tntBlue, tntOrange, tntPurple, tntYellow, tntPink, tntTeal];
      return seriesColors[index];
  }
}

function getSeries(distributionByDate, questionType, intl) {
  if (!Array.isArray(distributionByDate) || distributionByDate.length <= 0) {
    return {
      series: [],
      tooltipDateFormatter: () => null,
      xAxisDateFormatter: () => null,
    };
  }

  const options = (distributionByDate[0] && distributionByDate[0].distribution) || [];
  const categories = getCategories(questionType, options, intl);

  const firstDate = distributionByDate[0].date;
  const lastDate = distributionByDate[distributionByDate.length - 1].date;
  const monthsCount = moment(lastDate).diff(moment(firstDate), 'months');

  if (monthsCount < 3) {
    return {
      series: categories.map((category, categoryIndex) => {
        return {
          name: category,
          data: distributionByDate.map((distDateItem) => {
            return [
              Date.parse(distDateItem.date),
              distDateItem.distribution[categoryIndex].count,
            ];
          }),
          color: getSeriesColor(questionType, categoryIndex),
        };
      }),
      tooltipDateFormatter: ms => moment.utc(ms).format('dddd, D MMM Y'),
      xAxisDateFormatter: ms => moment.utc(ms).format('D MMM<br/>Y'),
    };
  } else if (monthsCount < 12) {
    return {
      series: categories.map((category, categoryIndex) => {
        return {
          name: category,
          data: distributionByDate.reduce((arr, item) => {
            const lastItemTimeMs = arr[arr.length - 1] && arr[arr.length - 1][0];
            const thisItemTimeMs = moment.utc(item.date).startOf('isoWeek').valueOf();

            if (lastItemTimeMs === thisItemTimeMs) {
              arr[arr.length - 1][1] += item.distribution[categoryIndex].count;
              return arr;
            } else {
              return arr.concat([[
                moment.utc(item.date).startOf('isoWeek').valueOf(),
                item.distribution[categoryIndex].count,
              ]]);
            }
          }, []),
          color: getSeriesColor(questionType, categoryIndex),
        };
      }),
      tooltipDateFormatter: ms => {
        return intl.formatMessage({ id: 'week' }) + moment.utc(ms).format(' W, Y');
      },
      xAxisDateFormatter: ms => {
        return intl.formatMessage({ id: 'week' }) + moment.utc(ms).format(' W<br/>Y');
      },
    };
  } else {
    return {
      series: categories.map((category, categoryIndex) => {
        return {
          name: category,
          data: distributionByDate.reduce((arr, item) => {
            const lastItemTimeMs = arr[arr.length - 1] && arr[arr.length - 1][0];
            const thisItemTimeMs = moment.utc(item.date).startOf('month').valueOf();

            if (lastItemTimeMs === thisItemTimeMs) {
              arr[arr.length - 1][1] += item.distribution[categoryIndex].count;
              return arr;
            } else {
              return arr.concat([[
                moment.utc(item.date).startOf('month').valueOf(),
                item.distribution[categoryIndex].count,
              ]]);
            }
          }, []),
          color: getSeriesColor(questionType, categoryIndex),
        };
      }),
      tooltipDateFormatter: ms => moment.utc(ms).format('MMMM Y'),
      xAxisDateFormatter: ms => moment.utc(ms).format('MMM<br/>Y'),
    };
  }
}

function getLegend(questionType) {
  let smileyImages = {
    1: saddestSmiley,
    2: sadSmiley,
    3: happySmiley,
    4: happiestSmiley,
  };

  if (questionType === QUESTION_TYPE_FIVESTARS) {
    smileyImages = {
      1: star1,
      2: star2,
      3: star3,
      4: star4,
      5: star5,
    };
  }

  switch (questionType) {
    case QUESTION_TYPE_FIVESTARS:
    case QUESTION_TYPE_SMILEYQUARTET:
    case QUESTION_TYPE_SMILEYCOMMENT:
      return {
        useHTML: true,
        labelFormatter: function () {
          return `<img 
            width="22px"
            height="22px"
            src="${smileyImages[this.name]}" />`;
        },
        squareSymbol: false,
        symbolPadding: 0,
        symbolWidth: 0,
        symbolHeight: 0,
      };
    default:
      return {};
  }
}

const styles = {
  wrapper: {
    padding: 15
  },
};

class DistributionByDateAreaChart extends Component {
  render() {
    const intl = this.props.intl;
    const question = this.props.question;
    const answersReport = this.props.answersReport;
    const distributionByDate = answersReport ? answersReport.distributionByDate : [];

    const {
      series,
      tooltipDateFormatter,
      xAxisDateFormatter,
    } = getSeries(distributionByDate, question.type, intl);

    const chartConfig = {
      chart: {
        type: 'area',
        height: this.props.height - (styles.wrapper.padding * 2),
        width: this.props.width - (styles.wrapper.padding * 2),
      },
      title: null,
      tooltip: {
        formatter: function () {
          const date = tooltipDateFormatter(this.x);
          const pointStrings = this.points.map(point => {
            const countMessage = intl.formatMessage({ id: 'numberOfAnswers' }, { count: point.y });
            const percentage = ReactHighcharts.Highcharts.formatSingle('.1f', point.percentage);
            return `
              <b style="color:${point.series.color}">${point.series.name}:</b>
              ${percentage}% (${countMessage})
            `;
          });
          return [date].concat(pointStrings);
        },
        split: true,
      },
      xAxis: {
        type: 'datetime',
        labels: {
          formatter: function () {
            return xAxisDateFormatter(this.value);
          },
          style: {
            color: grey500,
          },
        },
      },
      yAxis: {
        title: {
          text: null,
        },
        labels: {
          format: '{value}%',
          style: {
            color: grey500,
          },
        },
      },
      legend: getLegend(question.type),
      plotOptions: {
        area: {
          stacking: 'percent',
          lineColor: '#ffffff',
          lineWidth: 1,
          marker: {
            enabled: false,
          },
        },
      },
      series,
    };
    return (
      <div style={styles.wrapper}>
        <ReactHighcharts
          config={chartConfig}
          domProps={{ id: this.props.uniqueId }}
        />
      </div>
    );
  }
}

export default createFragmentContainer(
  injectIntl(DistributionByDateAreaChart),
  graphql`
    fragment DistributionByDateAreaChart_question on Question {
      type
    }
    fragment DistributionByDateAreaChart_answersReport on AnswersReport {
      distributionByDate {
        date
        count
        distribution {
          value
          count
          text {
            language
            text
          }
        }
      }
    }
  `,
);



// WEBPACK FOOTER //
// ./src/components/DistributionByDateAreaChart/DistributionByDateAreaChart.js